import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { NS } from "./U3101";
import {
  doCloneHourBankYearToNext,
  doCloneHourBankYearToNextBulk,
  doGetHourBankHistory,
  doUpdateHourBankYear,
  luHandleAppiError,
  parseU3101HourBankBulkCloneResultFromApi,
  parseU3101HourBankHistoryFromApi,
  type U3101HourBankBulkClonePayload,
  type U3101HourBankBulkCloneResult,
  type U3101HourBankHistory,
  type U3101HourBankYearUpdatePayload,
} from "./U3101Service";

export interface U3101HourBankHistoryHandlersOptions {
  onError?: (error: unknown) => void;
}

/**
 * Historique banques d’heures (BL-0315) : lecture, clone N→N+1, bulk liste, édition année.
 * Ne pas appeler les `do*` depuis les composants.
 */
export function useU3101HourBankHistoryHandlers(options: U3101HourBankHistoryHandlersOptions = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const { onError } = options;

  const handleError = useCallback(
    (error: unknown) => {
      if (onError) {
        onError(error);
        return;
      }
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      errorResult.genericErrors?.forEach((err) => toast.error(err.text, err.title));
    },
    [onError, t, toast],
  );

  const fetch = useCallback(
    async (employeeId: string): Promise<U3101HourBankHistory | null> => {
      const id = employeeId?.trim();
      if (!id) return null;
      try {
        const response = await doGetHourBankHistory(axios, id);
        return parseU3101HourBankHistoryFromApi(response.data);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  const cloneNext = useCallback(
    async (employeeId: string): Promise<U3101HourBankHistory | null> => {
      const id = employeeId?.trim();
      if (!id) return null;
      try {
        const response = await doCloneHourBankYearToNext(axios, id);
        return parseU3101HourBankHistoryFromApi(response.data);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  const cloneNextBulk = useCallback(
    async (payload: U3101HourBankBulkClonePayload): Promise<U3101HourBankBulkCloneResult | null> => {
      try {
        const response = await doCloneHourBankYearToNextBulk(axios, payload);
        return parseU3101HourBankBulkCloneResultFromApi(response.data);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  const updateYear = useCallback(
    async (
      employeeId: string,
      year: number,
      payload: U3101HourBankYearUpdatePayload,
    ): Promise<U3101HourBankHistory | null> => {
      const id = employeeId?.trim();
      if (!id) return null;
      try {
        const response = await doUpdateHourBankYear(axios, id, year, payload);
        return parseU3101HourBankHistoryFromApi(response.data);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  return { fetch, cloneNext, cloneNextBulk, updateYear };
}
